import { Close } from '@mui/icons-material';
import { Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Switch, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers';

import { useFormik } from 'formik';
import { useEffect } from 'react';
import AddImage from '../../../../components/AddImage';
import Address from '../../../../components/Address';
import SelectCategoriesParentChild from '../../../../components/SelectCategoriesParentChild';
import useDialog from '../../../../hooks/useDialog';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import DocumentInputMasked from '../../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../../components/PhoneInputMasked';
import SelectClub from '../../../../components/SelectClub';
import { BRAZIL_UF_OPTIONS, brazilUfValue } from '../../../../constants/brazilUf';

function Edit({ id, mutate }) {
    const { data } = useFetch(`advertiser/${id}`);
    return <Form data={data} mutate={mutate} />;
}

function Form({ data, mutate, mutateStats }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const { open, handleClose } = useDialog('#advertiser');

    const { values, handleSubmit, handleChange, setFieldValue, setFieldError, setValues, isSubmitting } = useFormik({
        initialValues: {
            name: '',
            businessName: '',
            description: '',
            image: '',
            document: '',
            headOfficeId: '',
            zipcode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            country: '',
            phone: '',
            areacode: '',
            latitude: '',
            longitude: '',
            address: '',
            status: true,
            listCategoryId: [],
            responsibleName: '',
            responsibleEmail: '',
            responsiblePhone: '',
            notes: '',
            contractExpiredAt: null,
            email: '',
            approved: true,
            clubId: null,
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                if (!Boolean(values.id)) {
                    setSubmitting(true);
                    await server.post(`advertiser`, values);
                    enqueueSnackbar(`Anunciante ${values.name} adicionado`);
                };

                if (Boolean(values.id)) {
                    setSubmitting(true);
                    await server.put(`advertiser/${values.id}`, values);
                    enqueueSnackbar(`Anunciante ${values.name} alterado`);
                    
                };
                await mutate();
                if (mutateStats) {  
                    await mutateStats();  
                };
                setSubmitting(false);
                handleClose();
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    useEffect(() => {
        if (data) {
            const listCategoryId = (data.Advertiser_category ?? [])
                .map(i => i.categoryId)
                .filter(id => id != null)
                .map(Number);
            setValues(
                {
                    ...data,
                    address: `${data.street}, ${data.neighborhood}, ${data.city}-${data.state}`,
                    listCategoryId,
                },
                true,
            );
        }
    }, [data]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <DialogTitle>Adicionar/editar anunciante</DialogTitle>
                {/* <IconButton size="small" onClick={e => openHelp('offer_avaliableQty')}>
                        <Help />
                    </IconButton> */}
                <IconButton size="small" onClick={handleClose} sx={{ marginRight: 2 }}>
                    <Close />
                </IconButton>
            </Stack>
            <Divider />
            <DialogContent>
                <Stack gap={2} component="form" onSubmit={handleSubmit}>
                    {Boolean(values.id) && !values.headOfficeId && (
                        <>
                            <AddImage
                                onChange={setFieldValue}
                                name="image"
                                value={values.previewImage || values.image}
                                canvaWidth={300}
                                canvaHeight={300}
                            />
                        </>
                    )}
                    <TextField onChange={handleChange} label="Nome do anunciante" name="name" value={values.name} />
                    <TextField onChange={handleChange} label="Razão social" name="businessName" value={values.businessName} />
                    <TextField
                        onChange={event => {
                            setFieldValue('document', event.target?.value || event);
                        }}
                        label="Documento"
                        name="document"
                        value={values.document}
                        InputProps={{
                            inputComponent: DocumentInputMasked,
                        }}
                    />

                    <TextField
                        onChange={event => {
                            setFieldValue('phone', event.target?.value || event);
                        }}
                        label="Telefone"
                        name="phone"
                        value={values.phone || ''}
                        InputProps={{
                            inputComponent: PhoneInputMasked,
                        }}
                    />

                    <TextField onChange={handleChange} label="Email de contato" name="email" value={values.email} />

                    {Boolean(values.id) && !values.headOfficeId && (
                        <>
                            <TextField onChange={handleChange} label="Website" name="website" value={values.website} />
                        </>
                    )}

                    <TextField onChange={handleChange} rows={10} multiline label="Sobre a loja" name="description" value={values.description} />

                    <SelectClub
                        name="clubId"
                        value={values.clubId || ''}
                        onChange={handleChange}
                        label="Indicado por clube"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Não significa que o anunciante pertence ao clube — só que foi indicado por ele.
                    </Typography>

                    <Address
                        onChange={handleChange}
                        setFieldValue={setFieldValue}
                        name="zipcode"
                        setFieldError={setFieldError}
                        value={values.zipcode}
                        address={values.address}
                    />
                    <Typography variant="caption" color="text.secondary">
                        O CEP preenche os campos se encontrar. Se não localizar ou vier incompleto, edite abaixo.
                    </Typography>
                    <TextField
                        fullWidth
                        onChange={handleChange}
                        label="Logradouro (rua, avenida…)"
                        name="street"
                        value={values.street || ''}
                    />
                    <TextField
                        fullWidth
                        onChange={handleChange}
                        label="Bairro"
                        name="neighborhood"
                        value={values.neighborhood || ''}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} columnGap={2} rowGap={2}>
                        <TextField fullWidth onChange={handleChange} label="Cidade" name="city" value={values.city || ''} />
                        <TextField
                            select
                            label="UF"
                            name="state"
                            value={brazilUfValue(values.state)}
                            onChange={event => setFieldValue('state', event.target.value)}
                            sx={{ width: { xs: '100%', sm: 220 } }}
                        >
                            {BRAZIL_UF_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <Stack direction="row" columnGap={2}>
                        <TextField fullWidth onChange={handleChange} label="Complemento" name="complement" value={values.complement} />
                        <TextField sx={{ width: 150 }} onChange={handleChange} label="Número" name="number" value={values.number} />
                    </Stack>

                    <Stack direction="row" columnGap={2}>
                        <TextField fullWidth onChange={handleChange} label="Latitude" name="latitude" value={values.latitude} />
                        <TextField fullWidth onChange={handleChange} label="Longitude" name="longitude" value={values.longitude} />
                    </Stack>

                    {/* Selecionando categorias pai + filhas + netas */}
                    {Boolean(values.id) && !values.headOfficeId && (
                        <>
                            <SelectCategoriesParentChild
                                value={values.listCategoryId}
                                name="listCategoryId"
                                onChange={setFieldValue}
                                label="Selecione as categorias"
                            />
                        </>
                    )}

                    {Boolean(values.id) && (
                        <>
                            <Divider>Dados internos</Divider>
                            <TextField onChange={handleChange} label="Nome do responsável" name="responsibleName" value={values.responsibleName} />
                            <TextField onChange={handleChange} label="Email do responsável" name="responsibleEmail" value={values.responsibleEmail} />
                            <TextField
                                onChange={event => {
                                    setFieldValue('responsiblePhone', event.target?.value || event);
                                }}
                                label="Telefone do responsável"
                                name="responsiblePhone"
                                value={values.responsiblePhone || ''}
                                InputProps={{
                                    inputComponent: PhoneInputMasked,
                                }}
                            />
                            <TextField onChange={handleChange} rows={5} multiline label="Observações" name="notes" value={values.notes} />

                            <DatePicker
                                label="Data de Expiração do Contrato"
                                value={values.contractExpiredAt ? new Date(values.contractExpiredAt) : null}
                                onChange={newValue => setFieldValue('contractExpiredAt', newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                    },
                                }}
                            />
                            <FormControlLabel
                                label="Status anunciante"
                                control={<Switch checked={values.status} onChange={e => setFieldValue('status', e.target.checked)} />}
                            />
                        </>
                    )}

                    {Boolean(values.id) && !values.headOfficeId && (
                        <FormControlLabel
                            label="Anunciante aprovado"
                            control={<Switch checked={values.approved ?? false} onChange={e => setFieldValue('approved', e.target.checked)} />}
                        />
                    )}

                    {Boolean(values.clubId) && values.Club?.name && (
                        <Chip size="small" color="primary" variant="outlined" label={`Indicado por ${values.Club.name}`} />
                    )}
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                {Boolean(values.id) && (
                    <Stack sx={{ marginRight: 'auto' }} spacing={0.5}>
                        <Typography fontSize={12}>Desde: {new Date(values.createdAt).toLocaleString('pt-BR')}</Typography>
                        {values.contractExpiredAt && (
                            <Typography fontSize={12}>Contrato expira em: {new Date(values.contractExpiredAt).toLocaleString('pt-BR')}</Typography>
                        )}
                         
                    </Stack>
                )}
                <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                    Salvar e continuar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function DialogAdvertiser({ mutate, mutateStats }) {
    const { open, id } = useDialog('#advertiser');
    if (open && id) return <Edit id={id} mutate={mutate} mutateStats={mutateStats} />;
    if (open) return <Form mutate={mutate} mutateStats={mutateStats} />;
}
